import { ref, shallowRef } from "vue";
import { loadStripe } from "@stripe/stripe-js";
import { useAuthStore, useDataStore } from "../stores";

const API_BASE = import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT;

const PAID_STATUSES = ["succeeded", "requires_capture"];
const STRIPE_CONFIG_KEY = "equidefi_stripe_config";

// Stripe's Payment Element runs in an iframe and can't read CSS custom properties,
// so we snapshot the current mode at init time and pass hardcoded hex values.
// Light: warm paper/beige palette. Dark: dark navy primary palette.
function getStripeAppearance() {
  const isDark = document.documentElement.classList.contains("dark");

  if (isDark) {
    return {
      theme: "night",
      variables: {
        colorPrimary: "#9ba8b2", // primary.1 — selected tabs, checkboxes
        colorBackground: "#012e40", // --bg-canvas dark (form area blends with page)
        colorText: "#ffffff", // --text-strongest dark
        colorTextSecondary: "#bfbfbf", // --text-strong dark
        colorTextPlaceholder: "#8c8c8c",
        colorDanger: "#fb7185", // --text-error dark
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        borderRadius: "8px",
        spacingUnit: "4px",
      },
      rules: {
        // Primary variant: input bg matches canvas (invisible) until focused
        ".Input": {
          backgroundColor: "#012e40", // --container-bg-dis-canvas dark = --bg-canvas dark
          border: "1px solid #012e40", // same as bg = invisible border
          boxShadow: "none",
          color: "#ffffff",
        },
        ".Input:focus": {
          border: "1px solid #e9f2ff", // --filter-outline-selected-chip-icon dark
          boxShadow: "none",
        },
        ".Input--invalid": { border: "1px solid #fb7185", boxShadow: "none" },
        ".Label": { color: "#bfbfbf", fontWeight: "500", fontSize: "13px" },
        ".Error": { color: "#fb7185" },
        ".Tab": {
          backgroundColor: "#012e40", // --bg-canvas dark
          border: "1px solid #516679", // --outline-light dark
          boxShadow: "none",
          color: "#bfbfbf",
        },
        ".Tab:hover": {
          backgroundColor: "#3a5165", // primary.4
          border: "1px solid #6a7c8c",
          color: "#ffffff",
        },
        ".Tab--selected": {
          backgroundColor: "#3a5165", // --tab-bg-selected dark
          border: "1px solid #9ba8b2",
          color: "#ffffff",
          boxShadow: "none",
        },
        ".TabIcon--selected": { fill: "#e9f2ff" },
        ".TabLabel--selected": { color: "#ffffff" },
        ".Block": {
          backgroundColor: "#012e40",
          border: "1px solid #516679",
          boxShadow: "none",
        },
      },
    };
  }

  return {
    theme: "night",
    variables: {
      colorPrimary: "#75613f", // paper.8 — selected tabs, checkboxes
      colorBackground: "#fffaf0", // --bg-canvas light (form area blends with page)
      colorText: "#141414", // --text-strongest light
      colorTextSecondary: "#595959", // --text-strong light
      colorTextPlaceholder: "#8c8c8c",
      colorDanger: "#be133c", // --text-error light
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      borderRadius: "8px",
      spacingUnit: "4px",
    },
    rules: {
      // Primary variant: input bg matches canvas (invisible) until focused
      ".Input": {
        backgroundColor: "#fffaf0", // --container-bg-dis-canvas light = --bg-canvas light
        border: "1px solid #fffaf0", // same as bg = invisible border
        boxShadow: "none",
        color: "#141414",
      },
      ".Input:focus": {
        border: "1px solid #75613f", // --filter-outline-selected-chip-icon light (paper.8)
        boxShadow: "none",
      },
      ".Input--invalid": { border: "1px solid #be133c", boxShadow: "none" },
      ".Label": { color: "#595959", fontWeight: "500", fontSize: "13px" },
      ".Error": { color: "#be133c" },
      ".Tab": {
        backgroundColor: "#fffaf0", // --bg-canvas light
        border: "1px solid #dfc695", // --outline-light light
        boxShadow: "none",
        color: "#595959",
      },
      ".Tab:hover": {
        backgroundColor: "#f4ebda",
        border: "1px solid #c9ad7d", // paper.7
        color: "#141414",
      },
      ".Tab--selected": {
        backgroundColor: "#dfc695", // --tab-bg-selected light
        border: "1px solid #75613f",
        color: "#141414",
        boxShadow: "none",
      },
      ".TabIcon--selected": { fill: "#75613f" },
      ".TabLabel--selected": { color: "#141414" },
      ".Block": {
        backgroundColor: "#fffaf0",
        border: "1px solid #dfc695",
        boxShadow: "none",
      },
    },
  };
}

export function useStripeCheckout() {
  const authStore = useAuthStore();
  const dataStore = useDataStore();
  const stripe = shallowRef(null);
  const elements = shallowRef(null);
  const paymentIntentId = ref(null);
  const loading = ref(false);
  const error = ref(null);

  async function initialize(container, { name, email, amount }) {
    loading.value = true;
    error.value = null;

    let api_body = { name, email, amount: Number(String(amount).replace(/[$,]/g, "")) };

    // console.log("body for stripe checkout", api_body);

    try {
      const res = await fetch(`${API_BASE}/checkout/payment-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          user_id: authStore.current_user.id,
          session_id: authStore.current_user.session_id,
        },
        body: JSON.stringify(api_body),
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(body?.message || body?.error || `Request failed (${res.status})`);
      }

      //  console.log("Stripe checkout init response", body);

      const { id, client_secret, publishable_key, stripe_account } = body;
      paymentIntentId.value = id;

      // Persist so we can re-initialize Stripe after a 3DS redirect (full page reload)
      sessionStorage.setItem(
        STRIPE_CONFIG_KEY,
        JSON.stringify({ publishable_key, stripe_account })
      );

      stripe.value = await loadStripe(publishable_key, { stripeAccount: stripe_account });

      elements.value = stripe.value.elements({
        clientSecret: client_secret,
        appearance: getStripeAppearance(),
      });

      const paymentElement = elements.value.create("payment", {
        layout: "tabs",
        wallets: {
          applePay: "auto",
          googlePay: "auto",
        },
      });

      // Guard: user may have switched payment methods while loadStripe/fetch were
      // in flight, causing the container to be removed from the DOM. Bail silently
      // rather than letting Stripe throw "Invalid DOM element".
      if (!container.isConnected) return;

      paymentElement.mount(container);
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function confirmPayment() {
    // console.log("Confirming Stripe payment intent - confirmPayment", paymentIntentId.value);
    if (!stripe.value || !elements.value) {
      throw new Error("Payment form not initialized.");
    }

    const { error: confirmError, paymentIntent } = await stripe.value.confirmPayment({
      elements: elements.value,
      redirect: "if_required",
      confirmParams: { return_url: window.location.href },
    });

    if (confirmError) {
      throw new Error(confirmError.message);
    }

    if (!PAID_STATUSES.includes(paymentIntent?.status)) {
      throw new Error(`Unexpected payment status: ${paymentIntent?.status ?? "unknown"}`);
    }

    paymentIntentId.value = paymentIntent.id;
    return paymentIntent;
  }

  async function checkRedirectReturn() {
    const params = new URLSearchParams(window.location.search);
    const cs = params.get("payment_intent_client_secret");
    if (!cs) return null;

    const stored = sessionStorage.getItem(STRIPE_CONFIG_KEY);
    if (!stored) return null;

    const { publishable_key, stripe_account } = JSON.parse(stored);
    const stripeInstance = await loadStripe(publishable_key, { stripeAccount: stripe_account });
    const { paymentIntent, error: retrieveError } = await stripeInstance.retrievePaymentIntent(cs);

    if (retrieveError) throw new Error(retrieveError.message);

    // Remove the client secret from the URL so a refresh doesn't re-trigger this
    params.delete("payment_intent_client_secret");
    const clean = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;
    window.history.replaceState({}, "", clean);

    return paymentIntent;
  }

  // Best-effort — releases the authorization hold on the cardholder's account.
  // Errors are swallowed so a network hiccup never blocks the user from continuing.
  async function cancelPaymentIntent(piId) {
    if (!piId?.startsWith("pi_")) return;
    try {
      // console.log("Canceling Stripe payment intent - cancelPaymentIntent", piId, {
      //   email: dataStore.contact.email,
      // });
      await fetch(`${API_BASE}/checkout/payment-intent/${piId}/cancel`, {
        method: "POST",
        headers: {
          user_id: authStore.current_user.id,
          session_id: authStore.current_user.session_id,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: dataStore.contact.email }),
      });
    } catch {}
  }

  // Returns true if the hold is still active, false if expired.
  // Fails safe (returns true) on any network/parse error to avoid wiping a valid PI.
  async function checkHoldStatus(piId) {
    if (!piId?.startsWith("pi_")) return false;
    try {
      const res = await fetch(`${API_BASE}/checkout/payment-intent/${piId}/status`, {
        headers: {
          user_id: authStore.current_user.id,
          session_id: authStore.current_user.session_id,
        },
      });
      if (!res.ok) return true;
      const { status } = await res.json().catch(() => ({}));
      return status === "requires_capture";
    } catch {
      return true;
    }
  }

  function updateAppearance() {
    elements.value?.update({ appearance: getStripeAppearance() });
  }

  function reset() {
    stripe.value = null;
    elements.value = null;
    paymentIntentId.value = null;
    loading.value = false;
    error.value = null;
    sessionStorage.removeItem(STRIPE_CONFIG_KEY);
  }

  return {
    stripe,
    elements,
    paymentIntentId,
    loading,
    error,
    initialize,
    confirmPayment,
    cancelPaymentIntent,
    checkHoldStatus,
    updateAppearance,
    checkRedirectReturn,
    reset,
  };
}
